import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  X, User, Home, Settings, LogOut, Shield, Crown, 
  CheckCircle, Users, Newspaper, CreditCard, MessageCircle, Mail
} from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';

export const MobileMenu = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  
  if (!isOpen || !user) return null;
  
  const handleLogout = () => {
    logout();
    onClose();
  };
  
  const menuItems = [
    { path: '/feed', icon: Newspaper, label: "Fil d'actualité" },
    { path: '/search', icon: Users, label: 'Rechercher' },
    { path: '/messages', icon: Mail, label: 'Messages' },
    { path: '/chat', icon: MessageCircle, label: 'Chat en direct', show: user.is_subscribed },
    { path: '/profile', icon: User, label: 'Mon Profil' },
    { path: '/relations', icon: Users, label: 'Mes Relations' },
    { path: '/subscription', icon: CreditCard, label: 'Abonnement', highlight: !user.is_subscribed },
    { path: '/validation', icon: CheckCircle, label: 'Validation', show: user.is_subscribed },
    { path: '/settings', icon: Settings, label: 'Paramètres' },
    { path: '/admin', icon: Shield, label: 'Administration', show: user.is_admin },
  ];
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 z-50 md:hidden"
        onClick={onClose}
      />
      
      {/* Menu Panel - adjusted for mobile bottom nav */}
      <div 
        className="fixed top-0 left-0 w-72 bg-dark-paper z-50 md:hidden flex flex-col transform transition-transform duration-300 ease-out"
        style={{ bottom: '64px' }}
        data-testid="mobile-menu"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
              <span className="text-dark font-serif text-xl">D</span>
            </div>
            <span className="font-serif text-xl gold-gradient-text">DiscreetLux</span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* User Info */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full border-2 border-gold/30 bg-dark-subtle flex items-center justify-center overflow-hidden">
              {user.profile_picture ? (
                <img 
                  src={getImageUrl(user.profile_picture)} 
                  alt={user.pseudo}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-gold text-lg">{user.pseudo?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium truncate">{user.pseudo}</span>
                {user.is_subscribed && <Crown className="w-4 h-4 text-gold flex-shrink-0" />}
                {/* Certifié badge removed - will be shown only when Veriff is integrated */}
              </div>
              <span className="text-xs text-gray-500">{user.email}</span>
            </div>
          </div>
          
          {!user.is_subscribed && (
            <Link 
              to="/subscription" 
              onClick={onClose}
              className="mt-3 block w-full text-center py-2 bg-gold text-dark font-semibold text-sm rounded hover:bg-gold-light transition-colors"
            >
              Abonnez-vous
            </Link>
          )}
        </div>
        
        {/* Menu Items */}
        <div className="py-2 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            if (item.show === false) return null;
            
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  isActive 
                    ? 'bg-gold/10 text-gold border-l-2 border-gold' 
                    : 'text-gray-300 hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                } ${item.highlight ? 'bg-gold/5' : ''}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-gold' : ''}`} />
                <span className="text-sm">{item.label}</span>
                {item.highlight && (
                  <span className="ml-auto text-[10px] bg-gold text-dark px-2 py-0.5 rounded-full font-medium">
                    PRO
                  </span>
                )}
              </Link>
            );
          })}
        </div>
        
        {/* Logout - Always visible at bottom */}
        <div className="p-4 border-t border-white/10 bg-dark-paper">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 text-red-400 hover:bg-red-500/10 rounded transition-colors"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">Déconnexion</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileMenu;
